import * as React from "react";
import * as _ from "underscore";
import { connect } from "react-redux";
import { Button } from "carbon-components-react";
import { Save16 } from "@carbon/icons-react";
import { AppShape, HurdleShape, DocTypes } from "@eave/shared/types/api";
import DocumentLink from "web_modules/underwriter_dashboard/DocumentViewer/DocumentLink";
import HurdleView from "underwriter_dashboard/shared/HurdleView";
import { createHurdleOverride, updateHurdleOverride } from "../actions";
import LabeledInput from "../../LabeledInput";

const styles = require("underwriter_dashboard/hurdle_editors/styles.module.scss");

interface HurdleEditorProps {
  dispatch?: Function;
  app: AppShape;
  hurdle: HurdleShape;
  onHide: Function;
}

class SpecialSituationsHurdleEditor extends React.Component<HurdleEditorProps, any> {
  constructor(props) {
    super(props);
    this.state = this.props.hurdle.override ? this.props.hurdle.override.values : {};
    this.postOverride = _.debounce(this.postOverride, 250);
  }

  onSave() {}

  docLinks(type) {
    const { app } = this.props;
    return _.filter(app.documents, (d: any) => d.type === type).map(doc => (
      <DocumentLink key={doc.id} appId={app.id} docId={doc.id}>
        {doc.original_filename}
      </DocumentLink>
    ));
  }

  buttons(field) {
    const value = this.props.hurdle.override ? this.props.hurdle.override.values[field] : null;
    return (
      <div className="button-menu horizontal">
        <Button
          onClick={this.postOverride.bind(this, field, true)}
          className={value === true ? "active" : null}
        >
          Yes
        </Button>
        <Button
          onClick={this.postOverride.bind(this, field, false)}
          className={value === false ? "active" : null}
        >
          No
        </Button>
      </div>
    );
  }

  handleOnChange(e) {
    const [field, value] = [e.target.name, e.target.value];
    this.setState({ [field]: value }, () => {
      this.postOverride(field, value);
    });
  }

  renderContent() {
    const { app } = this.props;
    return (
      <div>
        <ol>
          <li>
            <p>Do you intend to use a Power of Attorney at closing?</p>
            <p>{SpecialSituationsHurdleEditor.boolToYesNo(app.power_of_attorney)}</p>
            {this.docLinks(DocTypes.PowerOfAttorney)}
            {app.power_of_attorney && (
              <div>
                <div>
                  Is the Power of Attorney type eligible (i.e., Durable, Military, or General)?
                  {this.buttons("power_of_attorney_eligible")}
                </div>
                <div>
                  A Power of Attorney doc must contain the following:
                  <ul>
                    <li>Indicate clearly that the borrower is appointing an attorney-in-fact</li>
                    <li>Specifically identify the person being appointed</li>
                    <li>Specifically identify the property address</li>
                    <li>
                      Specify the borrower’s name exactly as it will appear on all closing documents
                    </li>
                    <li>Signed and dated by the borrower</li>
                    <li>
                      Appointed Attorney-in-Fact must be the same person signing the Note on behalf
                      of the borrower
                    </li>
                    <li>
                      Borrower name on the power of Attorney must match the borrower name on the
                      Note
                    </li>
                    <li>In effect prior to the Note date</li>
                    <li>
                      Include a statement that the power of Attorney is in full force and effect on
                      the closing date, survives subsequent disability (durable), and that
                      revocation must be in writing, or gives a specific expiration date which
                      survives the closing date
                    </li>
                    <li>
                      Must be notarized (if executed outside the U.S., must meet requirements in
                      power of Attorney Executed Abroad below)
                    </li>
                    <li>Bear the Recorder’s stamp, if previously recorded</li>
                  </ul>
                  <p>Is the Power of Attorney doc complete?</p>
                  {this.buttons("power_of_attorney_complete")}
                </div>
                {app.borrower2_ssn === "" && (
                  <div>
                    Is this use of Power of Attorney approved by Supervisor?
                    {this.buttons("power_of_attorney_approved_by_supervisor")}
                  </div>
                )}
                {app.has_borrower2 && (
                  <div>
                    Will at least one borrower be present at closing?
                    {this.buttons("at_least_one_borrower_present_at_closing")}
                  </div>
                )}
                <div>
                  Does Title Company approve of Power of Attorney?
                  {this.buttons("power_of_attorney_approved_by_title_company")}
                </div>
                <div>
                  <LabeledInput
                    inputType="text"
                    labelValue="Attorney-In-Fact First Name"
                    inputName="attorney_in_fact_first_name"
                    inputValue={this.state.attorney_in_fact_first_name}
                    onChange={this.handleOnChange.bind(this)}
                  />
                  <LabeledInput
                    inputType="text"
                    labelValue="Attorney-In-Fact Last Name"
                    inputName="attorney_in_fact_last_name"
                    inputValue={this.state.attorney_in_fact_last_name}
                    onChange={this.handleOnChange.bind(this)}
                  />
                </div>
              </div>
            )}
          </li>
          <li>
            <p>Do you intend to take title to the property in the name of a Trust?</p>
            <p>{SpecialSituationsHurdleEditor.boolToYesNo(app.title_in_trust)}</p>
            {this.docLinks(DocTypes.TrustAgreementOrCertification)}
            {app.title_in_trust && (
              <div>
                <ul>
                  <li>
                    Is occupancy primary or secondary home?
                    {this.buttons("occupancy_primary_or_secondary")}
                  </li>
                  <li>
                    Is the Trust one of the following: Blind, Irrevocable, Life Estate?
                    {this.buttons("trust_type_ineligible")}
                  </li>
                  <li>
                    Does the Trust certificate clearly identify the identities of the
                    grantor/trustor/settlor, beneficiaries, and trustee?
                    {this.buttons("trust_cert_specifies_identities")}
                  </li>
                  <li>
                    Does the Trust certificate clearly identify powers of the Trustee (sell, convey,
                    exchange, borrow, encumber)?
                    {this.buttons("trust_cert_specifies_powers")}
                  </li>
                  <li>
                    Does the Trust certificate clearly specify the property is held as part of a
                    trust?
                    {this.buttons("trust_cert_specifies_property_in_trust")}
                  </li>
                  <li>
                    Does the Trust certificate clearly specify the trust is revocable?
                    {this.buttons("trust_cert_specifies_revocable")}
                  </li>
                  <li>
                    Does the Trust certificate clearly specify the trust was created and became
                    effective during the lifetime of the original grantor/trustor/settlor?
                    {this.buttons("trust_cert_specifies_created_during_lifetime")}
                  </li>
                  <li>
                    Does the Trust certificate clearly specify the settlor as the primary
                    beneficiary?
                    {this.buttons("trust_cert_specifies_settlor_as_beneficiary")}
                  </li>
                  <li>
                    Does the Trust Certificate specify at least one of the Borrowers is the settlor
                    or the person who created the trust?
                    {this.buttons("trust_cert_specifies_borrower_is_settlor")}
                  </li>
                  <li>
                    Does the Trust Certificate specify the same Borrower is the trustee?
                    {this.buttons("trust_cert_specifies_borrower_is_trustee")}
                  </li>
                  <li>
                    Does the Trust Certificate specify at least one of the Borrowers is the
                    beneficiary or the person designated to benefit from the trust?
                    {this.buttons("trust_cert_specifies_borrower_is_beneficiary")}
                  </li>
                </ul>
              </div>
            )}
          </li>
          <li>
            <p>Do you have a spouse taking title along with you?</p>
            <p>{SpecialSituationsHurdleEditor.boolToYesNo(app.non_borrower_spouse_taking_title)}</p>
            {app.non_borrower_spouse_taking_title && (
              <div>
                <p>{app.non_borrower_spouse_full_legal_name}</p>
                <p>{app.non_borrower_spouse_address_line1}</p>
                <p>{app.non_borrower_spouse_address_line2}</p>
                <p>
                  {app.non_borrower_spouse_city},{app.non_borrower_spouse_state}{" "}
                  {app.non_borrower_spouse_postal_code}
                </p>
              </div>
            )}
            {this.docLinks(DocTypes.PhotoIdentification)}
          </li>
          <li>
            <p>
              Do you intend to take title as your sole and separate property (without your spouse)?
            </p>
            <p>{SpecialSituationsHurdleEditor.boolToYesNo(app.buy_out)}</p>
            {this.docLinks("buy_out")}
          </li>
        </ol>
      </div>
    );
  }

  static boolToYesNo(bool) {
    if (_.isNull(bool)) return "No response";
    return bool === true ? "Yes" : "No";
  }

  postOverride(field, value) {
    const { hurdle, dispatch, app } = this.props;
    const { override } = hurdle;
    const overrideValues = override && override.values ? override.values : {};
    overrideValues[field] = value;

    if (!override) {
      createHurdleOverride(dispatch, {
        appId: app.id,
        hurdleName: hurdle.name,
        hurdleOverrideValues: overrideValues,
      });
    } else if (override) {
      updateHurdleOverride(dispatch, {
        appId: app.id,
        hurdleOverrideId: hurdle.override.id,
        hurdleOverrideValues: overrideValues,
      });
    }
  }

  render() {
    const { app, hurdle, onHide } = this.props;

    return (
      <HurdleView app={app} hurdle={hurdle} onHide={onHide}>
        {this.renderContent()}
        <footer className={styles.footer}>
          <Button kind="secondary" onClick={onHide}>
            Cancel
          </Button>
          <Button kind="primary" onClick={this.onSave.bind(this)} renderIcon={Save16}>
            Save changes
          </Button>
        </footer>
      </HurdleView>
    );
  }
}

export default connect()(SpecialSituationsHurdleEditor);
